import React from 'react';
import PropTypes from 'prop-types';
import {ThemeProvider, StyledEngineProvider} from '@mui/material/styles';

import theme from '@core/theme';

/**
 * MuiProvider — minimal bootstrap wrapper for Material UI v9.
 *
 * Mounted at the application root (index.js) to enable:
 *   - ThemeProvider: exposes @core/theme (createTheme() defaults) to all MUI
 *     components in the tree, including PopupMessageProvider in ApplicationView.
 *   - StyledEngineProvider injectFirst: inserts Emotion <style> tags early in
 *     <head>, before webpack-bundled CSS (Semantic UI + custom SCSS). Later
 *     styles in the cascade can override MUI defaults where needed — e.g.
 *     _PopupMessage.scss for our only MUI component so far.
 *
 * Coexistence notes:
 *   - PopupMessageProvider.jsx relies on this provider plus _PopupMessage.scss,
 *     not per-component ThemeProvider wrapping.
 *   - CssBaseline is NOT rendered here while semantic-ui-css is still loaded.
 *     Two global resets can clash; that is our coexistence concern, not an MUI
 *     rule. Enable CssBaseline here after semantic-ui-css is removed (see also
 *     ScopedCssBaseline for incremental migration:
 *     https://mui.com/material-ui/react-css-baseline/).
 *   - Does not replace Redux <Provider> or ApolloProvider — wraps outside them.
 */

const MuiProvider = ({children}) => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

MuiProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default MuiProvider;
